import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { PropertyCard } from '@/components/property/PropertyCard';
import { FilterValues } from '@/components/property/PropertyFilters';
import { usePropertySearch } from '@/hooks/usePropertySearch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  LayoutGrid, List, ChevronLeft, ChevronRight, Search, X,
  ChevronDown, SlidersHorizontal, Home, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { ProjectAdsSidebar } from '@/components/property/ProjectAdsSidebar';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';

const PROPERTY_TYPES = ['villa', 'apartment', 'flat', 'penthouse', 'room', 'commercial', 'land'];
const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];
const BEDROOM_OPTIONS = ['1', '2', '3', '4', '5+'];

interface PropertyListingPageProps {
  listingType: 'sale' | 'rent';
  titleKey: string;
}

export function PropertyListingPage({ listingType, titleKey }: PropertyListingPageProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [layout, setLayout] = useState<'grid' | 'list'>('list');
  const [page, setPage] = useState(1);
  const searchQuery = searchParams.get('q') || '';
  const [filters, setFilters] = useState<FilterValues>(() => ({
    propertyType: searchParams.get('type') || undefined,
    city: searchParams.get('city') || undefined,
    sortBy: 'newest',
  }));
  const [moreOpen, setMoreOpen] = useState(false);

  const { data, isLoading } = usePropertySearch({ listingType, filters, page, query: searchQuery });

  const update = (key: keyof FilterValues, value: any) => {
    setFilters(f => ({ ...f, [key]: value === 'all' ? undefined : value }));
    setPage(1);
  };

  const activeFilterCount = [
    filters.propertyType,
    filters.city,
    filters.minPrice || filters.maxPrice,
    filters.minBedrooms,
    filters.minArea || filters.maxArea,
    filters.isFurnished,
    filters.hasParking,
  ].filter(Boolean).length;

  const clearAll = () => {
    setFilters({ sortBy: 'newest' });
    setPage(1);
  };

  return (
    <Layout>
      {/* Breadcrumb + Title bar */}
      <div className="border-b bg-muted/30">
        <div className="container px-3 md:px-6 lg:px-12 py-3 md:py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">{titleKey}</span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-xl md:text-2xl font-bold">{titleKey}</h1>
              {searchQuery && <p className="text-xs text-muted-foreground mt-0.5">Search: "{searchQuery}"</p>}
            </div>
            {data && (
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{data.total}</span> properties found
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Horizontal Filters Bar */}
      {/* Sticky offset: mobile = 56px header; md = 64px; lg = 40 top + 77 bottom = 117 */}
      <div className="sticky top-[56px] md:top-[64px] lg:top-[117px] z-30 bg-background border-b shadow-sm">
        <div className="container px-3 md:px-6 lg:px-12">
          <div className="flex items-center gap-2 py-2.5 overflow-x-auto scrollbar-hide">
            {/* City */}
            <Select value={filters.city || 'all'} onValueChange={v => update('city', v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(city => <SelectItem key={city} value={city} className="text-xs">{city}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Property Type */}
            <Select value={filters.propertyType || 'all'} onValueChange={v => update('propertyType', v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {PROPERTY_TYPES.map(type => (
                  <SelectItem key={type} value={type} className="text-xs capitalize">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {filters.minPrice || filters.maxPrice
                    ? `${filters.minPrice || '0'} - ${filters.maxPrice || '∞'} ETB`
                    : 'Price Range'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Price Range (ETB)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    className="h-9 text-xs"
                    value={filters.minPrice || ''}
                    onChange={e => update('minPrice', e.target.value || undefined)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    className="h-9 text-xs"
                    value={filters.maxPrice || ''}
                    onChange={e => update('maxPrice', e.target.value || undefined)}
                  />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[
                    { label: '<1M', min: undefined, max: '1000000' },
                    { label: '1-5M', min: '1000000', max: '5000000' },
                    { label: '5-10M', min: '5000000', max: '10000000' },
                    { label: '10M+', min: '10000000', max: undefined },
                  ].map(preset => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="sm"
                      className="h-7 text-[10px] flex-1"
                      onClick={() => {
                        setFilters(f => ({ ...f, minPrice: preset.min, maxPrice: preset.max }));
                        setPage(1);
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Bedrooms */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {filters.minBedrooms ? `${filters.minBedrooms}+ Beds` : 'Bedrooms'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Bedrooms</p>
                <div className="flex gap-1.5">
                  <Button
                    variant={!filters.minBedrooms ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-8 text-xs flex-1"
                    onClick={() => update('minBedrooms', undefined)}
                  >
                    Any
                  </Button>
                  {BEDROOM_OPTIONS.map(n => (
                    <Button
                      key={n}
                      variant={filters.minBedrooms === n ? 'secondary' : 'outline'}
                      size="sm"
                      className="h-8 text-xs flex-1"
                      onClick={() => update('minBedrooms', n)}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Area */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {filters.minArea || filters.maxArea
                    ? `${filters.minArea || '0'} - ${filters.maxArea || '∞'} m²`
                    : 'Area (m²)'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Area (m²)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={filters.minArea || ''} onChange={e => update('minArea', e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={filters.maxArea || ''} onChange={e => update('maxArea', e.target.value || undefined)} />
                </div>
              </PopoverContent>
            </Popover>

            {/* More Filters */}
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  More
                  {(filters.isFurnished || filters.hasParking) && (
                    <Badge className="h-4 w-4 p-0 flex items-center justify-center text-[9px] rounded-full ml-0.5">
                      {(filters.isFurnished ? 1 : 0) + (filters.hasParking ? 1 : 0)}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <p className="text-xs font-semibold mb-3">More Filters</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">{t('search.furnished')}</Label>
                    <Switch checked={filters.isFurnished || false} onCheckedChange={v => update('isFurnished', v || undefined)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">{t('search.parking')}</Label>
                    <Switch checked={filters.hasParking || false} onCheckedChange={v => update('hasParking', v || undefined)} />
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            {/* Sort */}
            <Select value={filters.sortBy || 'newest'} onValueChange={v => update('sortBy', v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('search.newest')}</SelectItem>
                <SelectItem value="price_asc">{t('search.priceLow')}</SelectItem>
                <SelectItem value="price_desc">{t('search.priceHigh')}</SelectItem>
                <SelectItem value="popular">{t('search.popular')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Layout Toggle */}
            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')}>
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')}>
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Filters Chips */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {filters.propertyType && (
                <Badge variant="secondary" className="text-[10px] gap-1 capitalize shrink-0">
                  {filters.propertyType}
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => update('propertyType', undefined)} />
                </Badge>
              )}
              {filters.city && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  {filters.city}
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => update('city', undefined)} />
                </Badge>
              )}
              {(filters.minPrice || filters.maxPrice) && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  {filters.minPrice || '0'} - {filters.maxPrice || '∞'} ETB
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { update('minPrice', undefined); update('maxPrice', undefined); }} />
                </Badge>
              )}
              {filters.minBedrooms && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  {filters.minBedrooms}+ Beds
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => update('minBedrooms', undefined)} />
                </Badge>
              )}
              {(filters.minArea || filters.maxArea) && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  {filters.minArea || '0'} - {filters.maxArea || '∞'} m²
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { update('minArea', undefined); update('maxArea', undefined); }} />
                </Badge>
              )}
              {filters.isFurnished && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  Furnished
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => update('isFurnished', undefined)} />
                </Badge>
              )}
              {filters.hasParking && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                  Parking
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => update('hasParking', undefined)} />
                </Badge>
              )}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={clearAll}>
                Clear All
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Property Grid + Sidebar */}
      <div className="container px-3 md:px-6 lg:px-12 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Left: Property Cards */}
          <div>
            {isLoading ? (
              <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className={layout === 'grid' ? 'h-72 rounded-xl' : 'h-36 rounded-xl'} />
                ))}
              </div>
            ) : data && data.properties.length > 0 ? (
              <>
                <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                  {data.properties.map((property: any) => (
                    <PropertyCard key={property.id} property={property} layout={layout} />
                  ))}
                </div>

                {data.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(data.totalPages, 5) }).map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'outline'}
                            size="icon"
                            className="h-9 w-9 text-xs"
                            onClick={() => setPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      {data.totalPages > 5 && (
                        <>
                          <span className="text-muted-foreground px-1">...</span>
                          <Button variant={page === data.totalPages ? 'default' : 'outline'} size="icon" className="h-9 w-9 text-xs" onClick={() => setPage(data.totalPages)}>
                            {data.totalPages}
                          </Button>
                        </>
                      )}
                    </div>
                    <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      Next <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-24">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold text-lg mb-1">{t('search.noResults')}</h3>
                <p className="text-sm text-muted-foreground">Try adjusting your filters or search criteria</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={clearAll}>Clear All Filters</Button>
              </div>
            )}
          </div>

          {/* Right: Project Ads Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-[160px]">
              <ProjectAdsSidebar />
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
